<template>
  <ActionPopUp v-if="authenticated" />
  <ThankYouPopUp :is-active="showThankYouMessage" @close="showThankYouMessage = false" />
  <div class="wrapper" :class="{ 
    'profile-page': isProfilePage,
    'rtl-layout': localeStore.currentLang === 'he',
    'ltr-layout': localeStore.currentLang === 'en'
  }">
    <MainBar v-if="authenticated && !isMobile" />
    <SmallNotification />
    <div class="wrapper__content">
      <Header v-if="authenticated" :class="{ 'header--collapsed': isHeaderCollapsed }" :data-collapsed="isHeaderCollapsed" />

      <Navigator v-if="isMobile && !isAuthRoute" :class="{ 'navigator--collapsed': isHeaderCollapsed }" :data-collapsed="isHeaderCollapsed" />

      <main ref="mainRef" class="main" :class="{ 
        main_opacity: !authenticated,
        'main--expanded': isHeaderCollapsed
      }">
        <router-view :key="refreshKey"></router-view>
      </main>
    </div>
    
    <!-- Mobile Bottom Navigation -->
    <Mob_BottomNav v-if="authenticated && isMobile && !isAuthRoute" />
    
    <!-- Mobile Floating Action Button -->
    <Mob_FloatingActionButton 
      v-if="authenticated && isMobile && !isAuthRoute && uiStore.floatingActionButton.visible && uiStore.floatingActionButton.onClick"
      :on-click="uiStore.floatingActionButton.onClick"
      :aria-label="uiStore.floatingActionButton.ariaLabel"
    />
  </div>
  <Loader :is-loading="loading" />
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import MainBar from '@/components/SideBar/MainBar.vue';
import Header from '@/components/Header/Header.vue';
import ThankYouPopUp from '@/layouts/ThankYouPopUp.vue';
import ActionPopUp from '@/components/Popups/Switcher.vue';
import SmallNotification from '@/components/UI/SmallNotification.vue';
import useLoadingStore from '@/store/loading';
import useLocaleStore from '@/store/locale';
import useAuthStore from '@/store/auth';
import Loader from '@/components/UI/Loader.vue';
import Navigator from '@/components/Header/Navigator.vue';
import Mob_BottomNav from '@/mobile_components/Mob_BottomNav.vue';
import Mob_FloatingActionButton from '@/mobile_components/Mob_FloatingActionButton.vue';
import { useUIStore } from '@/store/ui';
import { useRoute } from 'vue-router';
import { useMobileScrollDetection } from '@/common/composable/useMobileScrollDetection';

const localeStore = useLocaleStore();
const uiStore = useUIStore();
const route = useRoute();

const refreshKey = ref<number>(0);
const showThankYouMessage = ref<boolean>(false);

const isMobile = computed(() => uiStore.isMobile);
const authenticated = computed<boolean>(() => useAuthStore().isLoggedIn);
const loading = computed<boolean>(() => useLoadingStore().isLoading);
const isAuthRoute = computed(() => route.path.startsWith('/auth'));
const isProfilePage = computed(() => route.path.includes('/user/settings/profile'));

// Use shared scroll detection composable
const { mainRef, isHeaderCollapsed } = useMobileScrollDetection();

watch(
  () => localeStore.currentLang,
  () => {
    refreshKey.value += 1;
  }
);
</script>

<style lang="scss">
@use '@/assets/styles/btn.scss';
@use '@/assets/styles/main-page.scss';
@use '@/assets/styles/auth-form.scss';
@use '@/assets/styles/utils/icons';
@use '@/assets/styles/utils/toast';
@use '@/assets/styles/tooltip.scss';
@use '@/assets/styles/popup-actions.scss';
@use '@/assets/styles/popup.scss';
@use '@/assets/styles/player.scss';
@use '@/assets/styles/table-site.scss';
@use '@/assets/styles/profile-card.scss';
@use '@/assets/styles/color-edit.scss';
@use '@/assets/styles/credit-history.scss';
@use '@/assets/styles/file-upload.scss';
@use '@/assets/styles/meeting.scss';
@use '@/assets/styles/create-campaign.scss';
//@import '@/assets/styles/responsive/mob_sidebar';
//@import '@/assets/styles/responsive/mob_player.scss';
//@import '@/assets/styles/responsive/mob_history-modal';
@use '@/assets/styles/select-dropdown.scss';

@use '@/assets/styles/form-site.scss';
@use '@/assets/styles/settings.scss';
@use '@/assets/styles/modal-main.scss';
@use '@/assets/styles/add-block.scss';
@use '@/assets/styles/modal.scss';
@use '@/assets/styles/responsive/mob_sidebar.scss';

@media only screen and (max-width: 991px) {
  .header--collapsed {
    transform: translate3d(0, -100%, 0);
    transition: transform 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    will-change: transform;
  }
  
  .navigator--collapsed {
    transform: translate3d(0, -100%, 0);
    transition: transform 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    will-change: transform;
  }
  
  .main--expanded {
    padding-top: 0 !important;
    border-radius: 0 !important;
    // height: 100vh !important;
    transform: translate3d(0, 0, 0);
    transition: all 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    will-change: transform, height, padding-top;
  }
  
      .header, .navigator {
        will-change: transform;
        backface-visibility: hidden;
        -webkit-backface-visibility: hidden;
        // z-index: 1000;
      }
  
  .main {
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
    scroll-behavior: smooth;
  }
  
  body {
    overscroll-behavior: none;
    -webkit-overflow-scrolling: touch;
  }
  
  // Prevent zoom on mobile devices
  @media (max-width: 768px) {
    // Prevent zoom on input focus (iOS Safari)
    input, textarea, select {
      font-size: 16px !important;
      -webkit-appearance: none;
      appearance: none;
      border-radius: 0;
    }
    
    // Prevent double-tap zoom
    * {
      touch-action: manipulation;
    }
    
    // Prevent zoom on form elements
    input[type="text"],
    input[type="email"],
    input[type="tel"],
    input[type="password"],
    input[type="search"],
    input[type="url"],
    textarea,
    select {
      font-size: 16px !important;
      -webkit-text-size-adjust: 100%;
      -ms-text-size-adjust: 100%;
    }
  }
}
</style>
